import { isNotUndefined } from "@faire/typescript";
import { useMemo } from "react";

import { useAchProactiveLimitIncreaseGate } from "./gates/useAchProactiveLimitIncreaseGate";
import { useAchTermsPreApprovalGate } from "./gates/useAchTermsPreApprovalGate";
import { useBrandConsolidationGate } from "./gates/useBrandConsolidationGate";
import { useConductorGate } from "./gates/useConductorGate";
import { useFaireDirectIncentiveGate } from "./gates/useFaireDirectIncentiveGate";
import { useFaireDirectMarketplaceDiscountGate } from "./gates/useFaireDirectMarketplaceDiscountGate";
import { useFairePayDefaultedGate } from "./gates/useFairePayDefaultedGate";
import { useFirstOrderIncentiveGate } from "./gates/useFirstOrderIncentiveGate";
import { useInsiderCanAusLaunchGate } from "./gates/useInsiderCanAusLaunchGate";
import { useInsiderPartnershipLaunchAnnouncementGate } from "./gates/useInsiderPartnershipLaunchAnnouncementGate";
import { useInsiderRefreshReminderGate } from "./gates/useInsiderRefreshReminderGate";
import { useInsiderTermsAndConditionsAcceptanceGate } from "./gates/useInsiderTermsAndConditionsAcceptanceGate";
import { useNonBmInitialLimitLandingGate } from "./gates/useNonBmInitialLimitLandingGate";
import { useOneOffLimitIncreaseGate } from "./gates/useOneOffLimitIncreaseGate";
import { useOpeningOrderDiscountCountdownGate } from "./gates/useOpeningOrderDiscountCountdownGate";
import { useOpeningOrderDiscountGate } from "./gates/useOpeningOrderDiscountGate";
import { usePastDueInvoiceGate } from "./gates/usePastDueInvoiceGate";
import { usePaymentDisputeGate } from "./gates/usePaymentDisputeGate";
import { usePaymentPlanFullyPaidGate } from "./gates/usePaymentPlanFullyPaidGate";
import { usePaymentPlanPaymentFailedGate } from "./gates/usePaymentPlanPaymentFailedGate";
import { usePostOrderInsiderEnrollmentGate } from "./gates/usePostOrderInsiderEnrollmentGate";
import { usePrepaidCardsRestrictedGate } from "./gates/usePrepaidCardsRestrictedGate";
import { useSaleAnnouncementGate } from "./gates/useSaleAnnouncementGate";
import { useShopifyExistingRetailerBenefitsGate } from "./gates/useShopifyExistingRetailerBenefitsGate";
import { useShopifyNewRetailerBenefitsGate } from "./gates/useShopifyNewRetailerBenefitsGate";
import { useTermsPreApprovalGate } from "./gates/useTermsPreApprovalGate";
import { useUpdateBusinessInfoGate } from "./gates/useUpdateBusinessInfoGate";
import { useVolumePricingAnnouncementGate } from "./gates/useVolumePricingAnnouncementGate";
import { useWarehouseSaleGate } from "./gates/useWarehouseSaleGate";

export const useAllEnabledModalTypes = () => {
  const achProactiveLimitIncreaseGate = useAchProactiveLimitIncreaseGate();
  const achTermsPreApprovalGate = useAchTermsPreApprovalGate();
  const brandConsolidationGate = useBrandConsolidationGate();
  const conductorGate = useConductorGate();
  const faireDirectIncentiveGate = useFaireDirectIncentiveGate();
  const faireDirectMarketplaceDiscountGate =
    useFaireDirectMarketplaceDiscountGate();
  const fairePayDefaultedGate = useFairePayDefaultedGate();
  const firstOrderIncentiveGate = useFirstOrderIncentiveGate();
  const insiderCanAusLaunchGate = useInsiderCanAusLaunchGate();
  const insiderPartnershipLaunchAnnouncementGate =
    useInsiderPartnershipLaunchAnnouncementGate();
  const insiderRefreshReminderGate = useInsiderRefreshReminderGate();
  const insiderTermsAndConditionsAcceptanceGate =
    useInsiderTermsAndConditionsAcceptanceGate();
  const nonBmInitialLimitLandingGate = useNonBmInitialLimitLandingGate();
  const oneOffLimitIncreaseGate = useOneOffLimitIncreaseGate();
  const openingOrderDiscountCountdownGate =
    useOpeningOrderDiscountCountdownGate();
  const openingOrderDiscountGate = useOpeningOrderDiscountGate();
  const pastDueInvoiceGate = usePastDueInvoiceGate();
  const paymentDisputeGate = usePaymentDisputeGate();
  const paymentPlanFullyPaidGate = usePaymentPlanFullyPaidGate();
  const paymentPlanPaymentFailedGate = usePaymentPlanPaymentFailedGate();
  const postOrderInsiderEnrollmentGate = usePostOrderInsiderEnrollmentGate();
  const prepaidCardsRestrictedGate = usePrepaidCardsRestrictedGate();
  const saleAnnouncementGate = useSaleAnnouncementGate();
  const shopifyExistingRetailerBenefitsGate =
    useShopifyExistingRetailerBenefitsGate();
  const shopifyNewRetailerBenefitsGate = useShopifyNewRetailerBenefitsGate();
  const termsPreApprovalGate = useTermsPreApprovalGate();
  const updateBusinessInfoGate = useUpdateBusinessInfoGate();
  const volumePricingAnnouncementGate = useVolumePricingAnnouncementGate();
  const warehouseSaleGate = useWarehouseSaleGate();

  return useMemo(
    () =>
      [
        achProactiveLimitIncreaseGate,
        achTermsPreApprovalGate,
        brandConsolidationGate,
        conductorGate,
        faireDirectIncentiveGate,
        faireDirectMarketplaceDiscountGate,
        fairePayDefaultedGate,
        firstOrderIncentiveGate,
        insiderCanAusLaunchGate,
        insiderPartnershipLaunchAnnouncementGate,
        insiderRefreshReminderGate,
        insiderTermsAndConditionsAcceptanceGate,
        nonBmInitialLimitLandingGate,
        oneOffLimitIncreaseGate,
        openingOrderDiscountCountdownGate,
        openingOrderDiscountGate,
        pastDueInvoiceGate,
        paymentDisputeGate,
        paymentPlanFullyPaidGate,
        paymentPlanPaymentFailedGate,
        postOrderInsiderEnrollmentGate,
        prepaidCardsRestrictedGate,
        saleAnnouncementGate,
        shopifyExistingRetailerBenefitsGate,
        shopifyNewRetailerBenefitsGate,
        termsPreApprovalGate,
        updateBusinessInfoGate,
        volumePricingAnnouncementGate,
        warehouseSaleGate,
      ].filter(isNotUndefined),
    [
      achProactiveLimitIncreaseGate,
      achTermsPreApprovalGate,
      brandConsolidationGate,
      conductorGate,
      faireDirectIncentiveGate,
      faireDirectMarketplaceDiscountGate,
      fairePayDefaultedGate,
      firstOrderIncentiveGate,
      insiderCanAusLaunchGate,
      insiderPartnershipLaunchAnnouncementGate,
      insiderRefreshReminderGate,
      insiderTermsAndConditionsAcceptanceGate,
      nonBmInitialLimitLandingGate,
      oneOffLimitIncreaseGate,
      openingOrderDiscountCountdownGate,
      openingOrderDiscountGate,
      pastDueInvoiceGate,
      paymentDisputeGate,
      paymentPlanFullyPaidGate,
      paymentPlanPaymentFailedGate,
      postOrderInsiderEnrollmentGate,
      prepaidCardsRestrictedGate,
      saleAnnouncementGate,
      shopifyExistingRetailerBenefitsGate,
      shopifyNewRetailerBenefitsGate,
      termsPreApprovalGate,
      updateBusinessInfoGate,
      volumePricingAnnouncementGate,
      warehouseSaleGate,
    ]
  );
};
